import BtnRate from "@/components/common/BtnRate";
import { Button } from "@/components/common/Button";
import Skeleton from "@/components/common/Skeleton";
import { useUserStore } from "@/modules/auth/store/user";
import { formatNumber } from "@/modules/auth/utils/format";
import { DepositsItem } from "@/modules/user/types/wallet";
import { AnalyticsEvents } from "@/shared/utils/analytics/events";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

const Deposit = () => {
  const { t } = useTranslation();
  const { activated, isDepositLoading, deposits } = useUserStore();
  const stakingDisabled = true;
  const navigate = useNavigate();

  const goToMyDeposits = () => {
    navigate("/my-deposits");
  };

  const aggregateDepositsByRate = (deposits: DepositsItem[]) => {
    const flat = deposits
      .filter(
        (d) => d.deposit.status === "Created" || d.deposit.status === "Active",
      )
      .map((d: DepositsItem) => ({
        ...d.deposit,
        createdAt: new Date(d.deposit.createdAt),
        rate: d.deposit.tokenUsdPrice,
      }));

    // rate -> totalTokenAmount
    const byRate = new Map<number, number>();

    for (const d of flat) {
      const prev = byRate.get(d.rate) ?? 0;
      byRate.set(d.rate, prev + d.tokenAmount);
    }

    return byRate;
  };

  const totalUsd = Array.from(
    aggregateDepositsByRate(deposits).entries(),
  ).reduce((sum, [rate, totalAmount]) => {
    return sum + rate * totalAmount;
  }, 0);

  const totalToken = deposits
    .filter(
      (d) => d.deposit.status === "Created" || d.deposit.status === "Active",
    )
    .reduce((sum, d) => sum + d.deposit.tokenAmount, 0);

  return (
    <div className="flex flex-col gap-6 pt-6 pb-2">
      <div className="flex flex-col gap-1 items-center">
        <p className="text-[1.0625rem] leading-[1.375rem] font-medium text-text">
          {t("main-page.total_deposit")}
        </p>
        {isDepositLoading ? (
          <>
            <Skeleton className="h-12 w-20" />
            <Skeleton className="h-5.5 w-10" />
          </>
        ) : (
          <>
            <p className="text-[2.5rem] leading-[3rem] font-bold text-text">
              ${formatNumber(totalUsd)}
            </p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <p className="text-[1.0625rem] leading-[1.375rem] text-hint">
                {formatNumber(totalToken)}&nbsp;BTN
              </p>
              <BtnRate />
            </div>
          </>
        )}
      </div>
      <div className="flex items-center justify-center w-full px-4 gap-4 [@media(max-width:379px)]:flex-col">
        <Button
          onClick={() => undefined}
          text={t("main-page.add_deposit")}
          icon="add-circle"
          className="w-full"
          disabled={stakingDisabled || !activated || isDepositLoading}
          analyticsEvent={AnalyticsEvents.MainPage.AddDeposit}
        />
        {deposits.length > 0 && (
          <Button
            onClick={goToMyDeposits}
            text={t("main-page.my_deposits")}
            mode="bezeled"
            icon="reorder"
            className="[@media(max-width:379px)]:w-full"
            disabled={!activated || isDepositLoading}
            analyticsEvent={AnalyticsEvents.MainPage.ViewDeposits}
          />
        )}
      </div>
    </div>
  );
};

export default Deposit;
